// Path: src/components/aols/aeo/OLSupdates/OLS2900/OLS2953.tsx
// Improved post by replacing wording with numbers for referral levels, enlarging icons and arrows, and styling 5% commission text with a green background and larger font
// 05/25/25 05:51 PM
import React from "react";
import { Layout, Typography, Breadcrumb } from "antd";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { HomeOutlined } from "@ant-design/icons";
import { InlineShareButtons } from "sharethis-reactjs";

const { Title, Paragraph } = Typography;

const UpdateContainer = styled.div`
  background: #000000;
  min-height: 100vh;
  padding: 40px 20px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const UpdateContent = styled.div`
  max-width: 800px;
  width: 100%;
  background: #1a1a1a;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  text-align: center;
`;

const UpdateTitle = styled(Title)`
  && {
    color: #ffcc00 !important;
    margin-bottom: 30px;
  }
`;

const UpdateParagraph = styled(Paragraph)`
  && {
    color: #fff !important;
    font-size: 16px;
    line-height: 1.8;
    margin-bottom: 40px; /* Increased spacing between paragraphs */
  }
`;

const UpdateLink = styled.a`
  color: #ffaa00 !important;
  text-decoration: none;
  font-size: 16px;
  transition: color 0.3s ease;

  &:hover {
    color: #ffcc00 !important;
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #40c4ff;
    outline-offset: 2px;
  }
`;

const HighlightedText = styled.span`
  font-size: 48px;
  color: #ffcc00;
`;

const CommissionHighlight = styled.span`
  font-size: 28px; /* Enlarged font for 5% */
  font-weight: bold;
  background-color: #008000; /* Green background */
  color: #ffffff;
  padding: 5px 10px;
  border-radius: 8px;
`;

const UpdateImage = styled.img`
  max-width: 100%;
  border-radius: 8px;
  margin: 20px 0;
`;

const ReferralList = styled.ul`
  text-align: left;
  margin: 20px 0;
  padding-left: 20px;
`;

const ReferralItem = styled.li`
  margin-bottom: 20px; /* Increased spacing between list items */
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const ReferralLevel = styled.span`
  font-size: 24px; /* Larger numbers */
  font-weight: bold;
  color: #ffcc00;
`;

const ReferralIcon = styled.span`
  font-size: 32px; /* Enlarged icons */
`;

const ReferralArrow = styled.span`
  font-size: 28px; /* Enlarged arrows */
  color: #ffaa00;
  margin: 10px 0;
  display: block;
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const ShareThisWrapper = styled.div`
  margin-top: 30px; /* More spacing */
  padding: 10px;
  background: #1a1a1a;
  border-radius: 8px;
  display: flex;
  justify-content: center;

  .st-btn {
    border: 1px solid #ffcc00 !important;
    border-radius: 4px !important;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.05);
    }

    &:focus {
      outline: 2px solid #40c4ff;
      outline-offset: 2px;
    }
  }

  .st-total {
    color: #fff !important;
    font-size: 16px;
  }
`;

const OLS2953: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: "OLS Referral Program: 5% Promotion Tool to Receive Extra Commission",
    description:
      "Join the OLS Referral Program and earn a 5% commission on first-line referrals, with ongoing commissions up to five levels! Be part of the Sun Light Civilization movement with onelightsystem OLS.",
    url: "https://www.olsme.com/olsupdates/ols2900/2953",
    datePublished: "2025-05-25",
    author: {
      "@type": "Organization",
      name: "onelightsystem OLS",
      url: "https://www.olsme.com",
    },
    publisher: {
      "@type": "Organization",
      name: "onelightsystem OLS",
      url: "https://www.olsme.com",
      logo: "https://www.olsme.com/logo.jpg",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://www.olsme.com/olsupdates/ols2900/2953",
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.olsme.com",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "OLS Updates",
          item: "https://www.olsme.com/olsupdates",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "OLS2900",
          item: "https://www.olsme.com/olsupdates/ols2900",
        },
        {
          "@type": "ListItem",
          position: 4,
          name: "OLS Referral Program: 5% Promotion Tool",
          item: "https://www.olsme.com/olsupdates/ols2900/2953",
        },
      ],
    },
  };

  return (
    <Layout>
      <Helmet>
        <title>OLS Referral Program: 5% Promotion Tool to Receive Extra Commission - onelightsystem OLS</title>
        <meta
          name="description"
          content="Join the OLS Referral Program and earn a 5% commission on first-line referrals, with ongoing commissions up to five levels! Be part of the Sun Light Civilization movement with onelightsystem OLS."
        />
        <meta
          name="keywords"
          content="OLS Referral Program, 5% Commission, Sun Light Meditation, OLS Promoter, Sun Light Civilization, onelightsystem OLS, referral commissions, meditation education"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="onelightsystem OLS" />
        <link
          rel="canonical"
          href="https://www.olsme.com/olsupdates/ols2900/2953"
        />
        <meta
          property="og:title"
          content="OLS Referral Program: 5% Promotion Tool to Receive Extra Commission - onelightsystem OLS"
        />
        <meta
          property="og:description"
          content="Join the OLS Referral Program and earn a 5% commission on first-line referrals, with ongoing commissions up to five levels! Be part of the Sun Light Civilization movement with onelightsystem OLS."
        />
        <meta
          property="og:url"
          content="https://www.olsme.com/olsupdates/ols2900/2953"
        />
        <meta property="og:type" content="article" />
        <meta
          property="og:image"
          content="https://via.placeholder.com/1200x630?text=OLS+Referral+Program+Announcement"
        />
        <meta
          property="og:image:alt"
          content="OLS Referral Program 5% Commission Announcement"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="onelightsystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta
          name="twitter:title"
          content="OLS Referral Program: 5% Promotion Tool to Receive Extra Commission - onelightsystem OLS"
        />
        <meta
          name="twitter:description"
          content="Join the OLS Referral Program and earn a 5% commission on first-line referrals, with ongoing commissions up to five levels! Be part of the Sun Light Civilization movement with onelightsystem OLS."
        />
        <meta
          name="twitter:image"
          content="https://via.placeholder.com/1200x630?text=OLS+Referral+Program+Announcement"
        />
        <meta
          name="twitter:image:alt"
          content="OLS Referral Program 5% Commission Announcement"
        />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <BreadcrumbWrapper>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="/">
                  <HomeOutlined /> Home
                </Link>
              ),
            },
            {
              title: <Link to="/olsupdates">OLS Updates</Link>,
            },
            {
              title: <Link to="/olsupdates/ols2900">OLS2900</Link>,
            },
            {
              title: "OLS Referral Program: 5% Promotion Tool",
            },
          ]}
        />
      </BreadcrumbWrapper>

      <UpdateContainer>
        <UpdateContent>
          <UpdateTitle level={1}>OLS Referral Program: <CommissionHighlight>5%</CommissionHighlight> Promotion Tool to Receive Extra Commission</UpdateTitle>
          <UpdateParagraph>
            We’re excited to announce a radiant new opportunity for the public to join the OneLightSystem (OLS) movement! Starting today, <HighlightedText>May 25, 2025</HighlightedText>, the OLS Referral Program offers a powerful <CommissionHighlight>5%</CommissionHighlight> promotion tool, allowing you to earn a commission on your 1st-line referrals. This initiative is designed to empower you to contribute to the global Sun Light Civilization while reaping ongoing benefits as far as your referral network can grow!
          </UpdateParagraph>
          <UpdateParagraph>
            <strong>Why Join the OLS Referral Program?</strong> By becoming an OLS Promoter, you can share the transformative power of Sun Light Meditation and earn commissions across multiple levels. Here’s how it works:
            <ReferralList>
              <ReferralItem><ReferralIcon>👤</ReferralIcon> <ReferralLevel>1st Line:</ReferralLevel> Earn a <CommissionHighlight>5%</CommissionHighlight> commission for each new user you refer who joins OLS.</ReferralItem>
              <ReferralArrow>↓</ReferralArrow>
              <ReferralItem><ReferralIcon>👥</ReferralIcon> <ReferralLevel>2nd Line:</ReferralLevel> Earn a 4% commission on referrals made by your 1st-line referrals.</ReferralItem>
              <ReferralArrow>↓</ReferralArrow>
              <ReferralItem><ReferralIcon>👥👥</ReferralIcon> <ReferralLevel>3rd Line:</ReferralLevel> Earn a 3% commission on 3rd-level referrals.</ReferralItem>
              <ReferralArrow>↓</ReferralArrow>
              <ReferralItem><ReferralIcon>👥👥👥</ReferralIcon> <ReferralLevel>4th Line:</ReferralLevel> Earn a 2% commission on 4th-level referrals.</ReferralItem>
              <ReferralArrow>↓</ReferralArrow>
              <ReferralItem><ReferralIcon>👥👥👥👥</ReferralIcon> <ReferralLevel>5th Line:</ReferralLevel> Earn a 1% commission on 5th-level referrals.</ReferralItem>
            </ReferralList>
            This multi-level structure ensures that your efforts to grow the OLS community continue to contribute to your success, no matter how far your referral network extends!
          </UpdateParagraph>
          <UpdateParagraph>
            <strong>Join the Movement:</strong> The OLS Referral Program is open to the public, offering additional benefits to those who join this important movement. By participating, you’re not only earning commissions but also helping to expand the Sun Light Civilization—a global initiative to align humanity with natural rhythms through meditation education. Learn more about the program at <UpdateLink href="/referral" aria-label="Explore the OLS Referral Program">olsme.com/referral</UpdateLink>.
          </UpdateParagraph>
          <UpdateParagraph>
            Don’t miss this opportunity to be part of OLS’s mission to foster a luminous, connected world! Whether you’re new to OLS or an existing member, you can start earning commissions today by sharing your unique referral link. For more details on becoming an OLS Promoter, visit <UpdateLink href="/OLSStudentProspect" aria-label="Join OLS as a student prospect">olsme.com/OLSStudentProspect</UpdateLink>. Let’s grow the Sun Light Civilization together!
          </UpdateParagraph>
          <UpdateImage
            src="https://via.placeholder.com/800x400?text=OLS+Referral+Program+Announcement"
            alt="OLS Referral Program 5% Commission Announcement"
          />

          <ShareThisWrapper>
            <InlineShareButtons
              config={{
                alignment: "center",
                color: "social",
                enabled: true,
                font_size: 14,
                labels: "cta",
                language: "en",
                networks: [
                  "whatsapp",
                  "linkedin",
                  "messenger",
                  "facebook",
                  "twitter",
                  "blogger",
                  "copy_link",
                  "pinterest",
                  "sms",
                ],
                padding: 20,
                radius: 3,
                size: 28,
                show_count: true,
                show_faces: true,
                style: "button",
                show_total: true,
                min_count: 10,
                url: "https://www.olsme.com/olsupdates/ols2900/2953",
                image:
                  "https://via.placeholder.com/1200x630?text=OLS+Referral+Program+Announcement",
                description:
                  "Join the OLS Referral Program and earn a 5% commission on first-line referrals, with ongoing commissions up to five levels! Be part of the Sun Light Civilization movement with onelightsystem OLS!",
                title: "OLS Referral Program: 5% Promotion Tool to Receive Extra Commission - onelightsystem OLS",
                message:
                  "Earn commissions with the OLS Referral Program! Get 5% on first-line referrals and more. Join the Sun Light Civilization movement with onelightsystem OLS!",
                subject: "OLS Referral Program: 5% Commission Opportunity!",
                username: "onelightsystem",
              }}
            />
          </ShareThisWrapper>
        </UpdateContent>
      </UpdateContainer>
    </Layout>
  );
};

export default OLS2953;